import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { uniqBy, isNumber } from 'lodash'
import { toast } from 'react-toastify'

import { rqKeys } from '@/react-query-keys'
import { useAppStore } from '@/store'
import { accountsForTesting, formatMoney, formatTs, getMs } from '@/helpers'
import { useOnlineStatus } from '@/helpers/hooks'
import { useTransactions } from '@/helpers/query/use-transactions'
import { useUserCards } from '@/helpers/query/use-user-cards'
import { apiRequestNew } from '@/helpers/request'
import {
  flushPendingArchiveQueueToServer,
  PENDING_ARCHIVE_UPDATED_EVENT,
  readPendingArchiveQueue,
  removePendingArchiveQueueItem,
  writePendingArchiveQueue,
} from '@/helpers/certificate-archive-offline-queue'
import {
  flushPendingRestoreQueueToServer,
  PENDING_RESTORE_UPDATED_EVENT,
  readPendingRestoreQueue,
  writePendingRestoreQueue,
} from '@/helpers/certificate-restore-offline-queue'
import {
  PENDING_PURCHASE_UPDATED_EVENT,
  readPendingPurchaseQueue,
  removePendingPurchaseQueueItem,
  toOptimisticPurchasedCertificate,
} from '@/helpers/certificate-purchase-pending-queue'
import { profile } from '@/profile'
import { useCertificatesRest } from '@/helpers/query/use-certificates-rest'
import type {
  T_CertificateCategoriesListRes,
  T_CertificateNominalsListRes,
  T_CertificatesListRes,
  T_UserCardBalanceRes,
} from '@/types/responses'
import { Button, Flex, Skeleton } from '@/components'
import type { E_CertificateTypes } from '@/enums'
import { E_ShareholderStatuses } from '@/enums'
import { OrganizationBanner } from '@/b-components/organization-banner/organization-banner'
import { TopUpBalanceDrawer, ChecksHistoryDrawer } from '@/b-components'
import type { T_CertificateNominalsList } from '@/types'
import fakeQRPng from '@/img/fake-qr.png?inline'
import {
  ExistCertificatesDrawer,
  CertificateDetailsDrawer,
  OrganizationsDrawer,
  BuyCertificateDrawer,
} from './components/certificates'
import { ShareholderArea } from './components/shareholder'
import { useOrganizationsDrawerStore } from './components/certificates/organizations-drawer/store'
import { TransactionsDrawer, TransactionsList } from './components/transactions/components'
import { NewsWidget } from './components/news'
import { ModalBecomeShareholderForFullAccess } from './components/modal-become-shareholder-for-full-access'
import { DashboardMessages } from './components/dashboard-messages'
import iconPlusSvg from './img/icon-plus.svg'
import iconArrowRightSvg from './img/icon-arrow-right.svg'
import iconBalanceWarningSvg from './img/icon-balance-warning.svg'
import {
  SC_PageHomeWrapper,
  SC_RoundedCard,
  SC_BalanceAndTransactionsWrapper,
  SC_BalanceCardsWrapper,
  SC_BalanceCardTop,
  SC_BalanceCardBottom,
  SC_LabelCommonBalance,
  SC_BalanceAmount,
  SC_BalancePlusDisabled,
  SC_GoToAllCertificates,
  SC_AddCertificate,
  SC_CertificateCard,
  SC_CreatedCertificatesList,
  SC_DenominationAndQR,
  SC_Denomination,
  SC_QR,
  SC_GoToAllTransactions,
  SC_SkeletonWrapper,
  SC_CertId,
} from './styled'


export const HomePage: FC = () => {
  const { hash } = useLocation()
  const isOnline = useOnlineStatus()
  const queryClient = useQueryClient()
  const logHomeCertFlow = (_phase: string, _payload: Record<string, unknown> = {}) => {
    void _phase
    void _payload
  }

  const userData = useAppStore((s) => s.userData)
  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const { data: userCardsList } = useUserCards({
    isAuthorized,
    userId: userData.id,
  })
  const userCards = Array.isArray(userCardsList)
    ? userCardsList.filter((card) => Number(card?.type) === 201)
    : []
  const userCardsCount = userCards.length
  const shouldShowCardBalance = userCardsCount > 0
  const userCardBalanceQueries = useQueries({
    queries: userCards.map((card) => ({
      queryKey: [ rqKeys.userCardBalance, userData.id, card.id ],
      queryFn: async () => {
        const requestStartedAt = Date.now()
        const cardBalanceQueryKey = [ rqKeys.userCardBalance, userData.id, card.id ] as const
        const cardBalanceRevQueryKey = [ rqKeys.userCardBalanceRev, userData.id, card.id ] as const

        const res = await apiRequestNew<T_UserCardBalanceRes>('GET', `/money/card/balance?id=${card.id}`)

        const cardBalanceRevTs = Number(queryClient.getQueryData<number>(cardBalanceRevQueryKey) || 0)
        if (Number.isFinite(cardBalanceRevTs) && cardBalanceRevTs > requestStartedAt) {
          const cachedCardBalance = queryClient.getQueryData<T_UserCardBalanceRes>(cardBalanceQueryKey)
          return cachedCardBalance || res
        }

        return res
      },
      enabled: !!isAuthorized && !!userData.id,
      refetchOnWindowFocus: true,
    })),
  })

  const { data: certificatesRestList } = useCertificatesRest(
    userData.id || undefined
  )

  useEffect(() => {
    if (hash === '#premium-subscriptions' && userData.id) {
      const element = document.getElementById('premium-subscriptions')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [ hash, userData.id ])

  const isShareholder = (
    userData?.shareholderStatusId !== E_ShareholderStatuses.None
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Want
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Waiting
  ) || (userData?.sourceCompanyId !== 0 && profile.functionalAll)


  const {
    data: transactionsList,
    isPending: isTransactionsListPending,
  } = useTransactions()

  const certificateCategoriesList = useAppStore((s) => s.certificateCategoriesList)
  const setCertificateCategoriesList = useAppStore((s) => s.setCertificateCategoriesList)

  const certificateNominalsList = useAppStore((s) => s.certificateNominalsList)
  const setCertificateNominalsList = useAppStore((s) => s.setCertificateNominalsList)

  const isOrganizationsDrawerVisible = useOrganizationsDrawerStore((s) => s.isOrganizationsDrawerVisible)
  const showOrganizationsDrawer = useOrganizationsDrawerStore((s) => s.showOrganizationsDrawer)
  const hideOrganizationsDrawer = useOrganizationsDrawerStore((s) => s.hideOrganizationsDrawer)

  // List of certificates
  const {
    data: certificatesList,
    isPending: isCertificatesListPending,
  } = useQuery<T_CertificatesListRes>({
    queryKey: [ rqKeys.certificatesList ],
    queryFn: () => apiRequestNew('GET', '/era/gift/certificate?offset=0&limit=9999'),
    // Certificates list should NOT refetch constantly.
    // Fetch on first mount (page reload) and when user explicitly opens the list / after purchase actions.
    staleTime: getMs('24h'),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  })

  // Список архивных сертификатов
  const {
    data: archivedCertificatesList,
  } = useQuery<T_CertificatesListRes>({
    queryKey: [ rqKeys.certificatesList, 'archived' ],
    queryFn: () => apiRequestNew('GET', '/era/gift/certificate?archive=y&offset=0&limit=9999'),
    staleTime: getMs('24h'),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  })

  // Список чеков в ожидании
  const {
    data: waitingChecks,
  } = useQuery({
    queryKey: [ rqKeys.waitingChecks, userData.id ],
    queryFn: async () => {
      if (!userData.id) return []

      // AUX_API_URL removed - returning empty array
      return []
    },
    enabled: !!userData.id,
  })

  const [ isArchivedCertificatesMode, setIsArchivedCertificatesMode ] = useState(false)
  const [ queueVersion, setQueueVersion ] = useState(0)
  const pendingArchiveSyncTimersRef = useRef<number[]>([])
  const pendingRestoreSyncTimersRef = useRef<number[]>([])
  const isPendingArchiveSyncInFlightRef = useRef(false)
  const pendingArchiveConfirmHitsRef = useRef<Record<number, number>>({})
  const setCertificatesListToArchivedMode = () => setIsArchivedCertificatesMode(true)
  const setCertificatesListToRegularMode = () => setIsArchivedCertificatesMode(false)

  // List of certificate categories
  const {
    data: certificateCategoriesListData,
    isSuccess: certificateCategoriesIsSuccess,
  } = useQuery<T_CertificateCategoriesListRes>({
    queryKey: [ rqKeys.certificateCategoriesList ],
    queryFn: () => apiRequestNew('GET', '/shop/goods/category'),
    staleTime: getMs('15m'),
    refetchOnWindowFocus: false,
  })

  // Set certificate categories list to store
  useEffect(() => {
    if (certificateCategoriesIsSuccess) {
      setCertificateCategoriesList(
        certificateCategoriesListData.filter((c) => c.parent)
      )
    }
  }, [ certificateCategoriesIsSuccess ])

  // Certificate nominals list
  const certificateNominalsListResponses = useQueries<T_CertificateNominalsList[]>({
    queries: (certificateCategoriesListData || [])
      .filter((category) => !!category.parent)
      .map((category) => ({
        queryKey: [ rqKeys.certificatesNominalsList, category.id ],
        queryFn: () => {
          return apiRequestNew<T_CertificateNominalsListRes>(
            'GET',
            `/shop/goods?category=${category.id}`,
          )
            .then((res) => ({
              categoryId: category.id,
              list: res,
            }))
        },
        enabled: !!certificateCategoriesListData,
        staleTime: getMs('15m'),
        refetchOnWindowFocus: false,
      })),
  })

  const isNominalsAlreadySet = useRef<boolean>(false)

  // Set certificate nominals list
  useEffect(() => {
    const results = certificateNominalsListResponses.map((cN) => cN.data as T_CertificateNominalsList)

    if (
      !isNominalsAlreadySet.current
      && results.length
      && results.every((r) => !!r)
    ) {
      isNominalsAlreadySet.current = true

      setCertificateNominalsList(
        certificateNominalsListResponses.map((cN) => cN.data as T_CertificateNominalsList)
      )
    }
  }, [ certificateNominalsListResponses ])

  const getCertificateType = (certificateNominalId: number) => {
    const allCertificateNominalsInRow = certificateNominalsList.reduce(
      (acc, cN) => ([
        ...acc,
        ...cN.list,
      ]),
      [] as T_CertificateNominalsListRes,
    )

    // TODO: Remove after fix API
    const onlyUniqueCertificateNominals = uniqBy(
      allCertificateNominalsInRow,
      'id',
    ) as T_CertificateNominalsListRes

    const foundCertificateNominal = onlyUniqueCertificateNominals.find(
      (cN) => cN.id === certificateNominalId
    )

    if (!foundCertificateNominal) return null

    const foundCategory = certificateCategoriesList.find(
      (c) => c.id === foundCertificateNominal.category
    )

    if (!foundCategory) return null

    return foundCategory.name
  }

  const getCertificateDenomination = (certificateNominalId: number) => {
    const allCertificateNominalsInRow = certificateNominalsList.reduce(
      (acc, cN) => ([
        ...acc,
        ...cN.list,
      ]),
      [] as T_CertificateNominalsListRes,
    )

    // TODO: Remove after fix API
    const onlyUniqueCertificateNominals = uniqBy(
      allCertificateNominalsInRow,
      'id',
    ) as T_CertificateNominalsListRes

    const foundNominal = onlyUniqueCertificateNominals.find((cN) => cN.id === certificateNominalId)

    if (!foundNominal) return null

    return foundNominal.price.base.rur
  }

  const normalizeCertTimeMs = (t: unknown) => {
    const n = Number(t)
    if (!Number.isFinite(n) || n <= 0) return 0
    return n < 1e12 ? n * 1000 : n
  }

  const buildPendingOrderMap = (userId: number | null | undefined) => {
    const map = new Map<number, number>()
    for (const item of readPendingArchiveQueue(userId)) {
      map.set(Number(item.cert.id), Math.max(map.get(Number(item.cert.id)) || 0, Number(item.movedAt) || 0))
    }
    for (const item of readPendingRestoreQueue(userId)) {
      map.set(Number(item.cert.id), Math.max(map.get(Number(item.cert.id)) || 0, Number(item.movedAt) || 0))
    }
    for (const item of readPendingPurchaseQueue(userId)) {
      map.set(Number(item.tempId), Math.max(map.get(Number(item.tempId)) || 0, Number(item.createdAt) || 0))
    }
    return map
  }

  const sortCertificatesForUi = useCallback((
    list: T_CertificatesListRes,
    pendingOrderMap?: Map<number, number>,
  ): T_CertificatesListRes => {
    const orderMap = pendingOrderMap || buildPendingOrderMap(userData.id)
    return [ ...(list || []) ].sort((a, b) => {
      const aPendingOrder = orderMap.get(Number(a.id))
      const bPendingOrder = orderMap.get(Number(b.id))
      const aPending = Number.isFinite(aPendingOrder)
      const bPending = Number.isFinite(bPendingOrder)
      if (aPending !== bPending) return aPending ? -1 : 1
      if (aPending && bPending && aPendingOrder !== bPendingOrder) {
        return Number(bPendingOrder) - Number(aPendingOrder)
      }

      const aTime = normalizeCertTimeMs(a.time)
      const bTime = normalizeCertTimeMs(b.time)
      if (aTime !== bTime) return bTime - aTime
      return Number(b.id) - Number(a.id)
    })
  }, [ userData.id ])

  const activeCertificatesForUi = useMemo(() => {
    void queueVersion
    const base = certificatesList || []
    const pendingArchiveIds = new Set(
      readPendingArchiveQueue(userData.id).map((p) => p.cert.id)
    )
    const pendingRestoreIds = new Set(
      readPendingRestoreQueue(userData.id)
        .filter((p) => !pendingArchiveIds.has(p.cert.id))
        .map((p) => p.cert.id)
    )
    const filtered = base.filter((c) => (
      !pendingArchiveIds.has(c.id) || pendingRestoreIds.has(c.id)
    ))
    return sortCertificatesForUi(filtered)
  }, [ certificatesList, userData.id, queueVersion, sortCertificatesForUi ])

  // Rest of certificates (from backend)
  const restMap = useMemo(() => {
    if (!certificatesRestList) return {}

    const map: Record<string, { value: number, ts: number }> = {}

    for (const item of certificatesRestList) {
      const key = `${item.certificateId}-${item.nominalId}`
      map[key] = {
        value: Number(item.amount),
        ts: Number(item.updatedAtTs),
      }
    }

    return map
  }, [ certificatesRestList ])

  const [ isModalBecomeShareholderOpen, setIsModalBecomeShareholderOpen ] = useState(false)
  const handleOk = () => setIsModalBecomeShareholderOpen(false)

  const guardShareholder = (action: () => void) => () => {
    if (isShareholder) action()
    else setIsModalBecomeShareholderOpen(true)
  }

  const guardOnline = (action: () => void) => () => {
    if (isOnline) {
      action()
    } else {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
    }
  }

  const safeRefreshCertificatesList = async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return

    try {
      logHomeCertFlow('safeRefresh:start', {
        pendingArchive: readPendingArchiveQueue(userData.id).length,
        pendingRestore: readPendingRestoreQueue(userData.id).length,
        pendingPurchase: readPendingPurchaseQueue(userData.id).length,
      })
      const cacheResetRef = { current: true } as any
      const [ serverActive, serverArchived ] = await Promise.all([
        apiRequestNew<T_CertificatesListRes>(
          'GET',
          '/era/gift/certificate?offset=0&limit=9999',
          undefined,
          cacheResetRef,
        ),
        apiRequestNew<T_CertificatesListRes>(
          'GET',
          '/era/gift/certificate?archive=y&offset=0&limit=9999',
        ),
      ])
      const serverArchivedIds = new Set((serverArchived || []).map((c) => c.id))
      const pendingArchiveIdsForRestoreFilter = new Set(
        readPendingArchiveQueue(userData.id).map((p) => p.cert.id)
      )
      const pendingRestoreIds = new Set(
        readPendingRestoreQueue(userData.id)
          .filter((p) => !pendingArchiveIdsForRestoreFilter.has(p.cert.id))
          .map((p) => p.cert.id)
      )

      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList ],
        (old) => {
          const normalizeTimeMs = (t: unknown) => {
            const n = Number(t)
            if (!Number.isFinite(n) || n <= 0) return 0
            return n < 1e12 ? n * 1000 : n
          }
          const recentRealGraceMs = getMs('15m')
          const now = Date.now()
          const pendingArchive = readPendingArchiveQueue(userData.id)
          const pendingArchiveIds = new Set(pendingArchive.map((p) => p.cert.id))
          const purchaseTempIds = new Set(
            readPendingPurchaseQueue(userData.id).map((p) => p.tempId)
          )
          const pending = (old || []).filter((c) => c.id < 0 && purchaseTempIds.has(c.id))
          const serverFiltered = (serverActive || []).filter((c) => (
            !pendingArchiveIds.has(c.id)
            && (!serverArchivedIds.has(c.id) || pendingRestoreIds.has(c.id))
          ))
          const serverIds = new Set(serverFiltered.map((c) => c.id))
          // Backend may be briefly eventually-consistent after purchase confirmation.
          // Keep recently issued real certs from cache for a short grace window.
          const fallbackRecentReal = (old || []).filter((c) => {
            if (!(Number(c.id) > 0)) return false
            if (pendingArchiveIds.has(c.id)) return false
            if (serverIds.has(c.id)) return false
            if (serverArchivedIds.has(c.id)) return false
            if (typeof c.code !== 'string' || c.code.trim() === '') return false
            const t = normalizeTimeMs(c.time)
            if (!t) return false
            return (now - t) <= recentRealGraceMs
          })
          const next = [ ...pending, ...serverFiltered, ...fallbackRecentReal ]
            .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
          return sortCertificatesForUi(next)
        }
      )
      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList, 'archived' ],
        sortCertificatesForUi(serverArchived || [])
      )

      applyPendingPurchaseQueueToCache(userData.id)
      // Keep optimistic archived items visible while pending queue is not confirmed by backend.
      applyPendingArchiveQueueToCache(userData.id)
      // Keep optimistic restored items visible while restore queue is not confirmed by backend.
      applyPendingRestoreQueueToCache(userData.id)
      logHomeCertFlow('safeRefresh:done', {
        serverActive: serverActive?.length || 0,
      })
    } catch {
      logHomeCertFlow('safeRefresh:error')
      // If request fails, fallback to best-effort refetch for both slices.
      void Promise.allSettled([
        queryClient.refetchQueries({ queryKey: [ rqKeys.certificatesList ], exact: true }),
        queryClient.refetchQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], exact: true }),
      ])
    }
  }
  const safeRefreshCertificatesListRef = useRef(safeRefreshCertificatesList)
  safeRefreshCertificatesListRef.current = safeRefreshCertificatesList

  const applyPendingPurchaseQueueToCache = useCallback((userId: number | null | undefined) => {
    const pending = readPendingPurchaseQueue(userId)
      .sort((a, b) => b.createdAt - a.createdAt)
    if (!pending.length) {
      // Defensive cleanup after reload: if queue is empty, remove stale optimistic
      // purchase placeholders from cache (negative ids are only for pending purchases).
      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList ],
        (old) => {
          const base = old || []
          const next = base.filter((c) => c.id > 0)
          return next.length === base.length ? old : next
        }
      )
      return
    }

    const normalizeTimeMs = (t: unknown) => {
      const n = Number(t)
      if (!Number.isFinite(n) || n <= 0) return 0
      return n < 1e12 ? n * 1000 : n
    }

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => {
        const base = old || []
        const real = base.filter((c) => Number(c.id) > 0)

        const nextPending = pending.filter((item) => {
          const pendingDocumentId = Number(item.documentId)
          const candidates = real.filter((c) => (
            Number(c.id) > 0
            && Number(c.goods) === Number(item.nominalId)
            && typeof c.code === 'string'
            && c.code.trim() !== ''
          ))
          const hasMatch = (() => {
            if (Number.isFinite(pendingDocumentId) && pendingDocumentId > 0) {
              const byDocumentId = candidates.some((c) => Number(c.document) === pendingDocumentId)
              if (byDocumentId) return true

              const allCandidatesWithoutDocument = candidates.every((c) => !(Number(c.document) > 0))
              const waitedLongEnough = (Date.now() - item.createdAt) >= getMs('3m')
              if (allCandidatesWithoutDocument && waitedLongEnough) {
                return candidates.some((c) => {
                  const t = normalizeTimeMs(c.time)
                  if (!t) return false
                  return Math.abs(t - item.createdAt) <= getMs('10m')
                })
              }
              return false
            }

            return candidates.some((c) => {
              const t = normalizeTimeMs(c.time)
              if (!t) return false
              return Math.abs(t - item.createdAt) <= getMs('10m')
            })
          })()
          const isTooOld = (Date.now() - item.createdAt) > getMs('60m')
          return !hasMatch && !isTooOld
        })

        for (const removed of pending) {
          if (!nextPending.some((x) => x.tempId === removed.tempId)) {
            removePendingPurchaseQueueItem(userId, removed.tempId)
          }
        }

        const keepTempIds = new Set(nextPending.map((p) => p.tempId))
        const purchaseTempIds = new Set(pending.map((p) => p.tempId))
        const next = base.filter((c) => (
          c.id > 0
          || !purchaseTempIds.has(c.id)
          || keepTempIds.has(c.id)
        ))
        let changed = next.length !== base.length
        for (const p of nextPending) {
          if (!next.some((c) => c.id === p.tempId)) {
            next.unshift(toOptimisticPurchasedCertificate(p))
            changed = true
          }
        }

        if (!changed) return old
        return sortCertificatesForUi(next)
      }
    )
  }, [ queryClient, sortCertificatesForUi ])

  const applyPendingArchiveQueueToCache = (userId: number | null | undefined) => {
    const pending = readPendingArchiveQueue(userId)
      .sort((a, b) => b.movedAt - a.movedAt)
    const pendingIds = new Set(pending.map((p) => p.cert.id))
    const pendingRestoreIds = new Set(
      readPendingRestoreQueue(userId)
        .filter((p) => !pendingIds.has(p.cert.id))
        .map((p) => p.cert.id)
    )
    const pendingFiltered = pending
      .filter((p) => !pendingRestoreIds.has(p.cert.id))
      .sort((a, b) => b.movedAt - a.movedAt)

    if (!pendingFiltered.length) return
    logHomeCertFlow('applyPendingArchiveQueueToCache', {
      pendingIds: pendingFiltered.map((p) => p.cert.id),
      pendingCount: pendingFiltered.length,
    })

    const pendingIdsFiltered = new Set(pendingFiltered.map((p) => p.cert.id))

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => sortCertificatesForUi((old || []).filter((c) => !pendingIdsFiltered.has(c.id)))
    )
    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList, 'archived' ],
      (old) => {
        const next = [ ...pendingFiltered.map((p) => p.cert), ...(old || []) ]
          .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
        return sortCertificatesForUi(next)
      }
    )
  }

  const applyPendingRestoreQueueToCache = (userId: number | null | undefined) => {
    const pendingArchiveIds = new Set(
      readPendingArchiveQueue(userId).map((p) => p.cert.id)
    )
    const pending = readPendingRestoreQueue(userId)
      .filter((p) => !pendingArchiveIds.has(p.cert.id))
      .sort((a, b) => b.movedAt - a.movedAt)
    if (!pending.length) return
    logHomeCertFlow('applyPendingRestoreQueueToCache', {
      pendingIds: pending.map((p) => p.cert.id),
      pendingCount: pending.length,
    })

    const pendingIds = new Set(pending.map((p) => p.cert.id))

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList, 'archived' ],
      (old) => sortCertificatesForUi((old || []).filter((c) => !pendingIds.has(c.id)))
    )
    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => {
        const next = [ ...pending.map((p) => p.cert), ...(old || []) ]
          .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
        return sortCertificatesForUi(next)
      }
    )
  }

  const clearPendingArchiveSyncTimers = () => {
    for (const t of pendingArchiveSyncTimersRef.current) window.clearTimeout(t)
    pendingArchiveSyncTimersRef.current = []
    pendingArchiveConfirmHitsRef.current = {}
  }
  const clearPendingRestoreSyncTimers = () => {
    for (const t of pendingRestoreSyncTimersRef.current) window.clearTimeout(t)
    pendingRestoreSyncTimersRef.current = []
  }

  const schedulePendingArchiveConfirmation = (userId: number | null | undefined) => {
    clearPendingArchiveSyncTimers()

    const delays = [
      0,
      getMs('1500ms'),
      getMs('4s'),
      getMs('8s'),
      getMs('16s'),
      getMs('30s'),
      getMs('2m'),
      getMs('5m'),
    ]

    for (const delay of delays) {
      const tid = window.setTimeout(async () => {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return
        if (isPendingArchiveSyncInFlightRef.current) return

        const pending = readPendingArchiveQueue(userId)
          .sort((a, b) => b.movedAt - a.movedAt)
        const pendingIds = new Set(pending.map((p) => p.cert.id))
        const pendingRestoreIds = new Set(
          readPendingRestoreQueue(userId)
            .filter((p) => !pendingIds.has(p.cert.id))
            .map((p) => p.cert.id)
        )
        const pendingFiltered = pending
          .filter((p) => !pendingRestoreIds.has(p.cert.id))
          .sort((a, b) => b.movedAt - a.movedAt)
        if (!pendingFiltered.length) return
        logHomeCertFlow('schedulePendingArchiveConfirmation:tick', {
          delay,
          pendingIds: pendingFiltered.map((p) => p.cert.id),
        })

        isPendingArchiveSyncInFlightRef.current = true
        try {
          const normalizeTimeMs = (t: unknown) => {
            const n = Number(t)
            if (!Number.isFinite(n) || n <= 0) return 0
            return n < 1e12 ? n * 1000 : n
          }
          const recentRealGraceMs = getMs('15m')
          const now = Date.now()
          const [ serverActive, serverArchived ] = await Promise.all([
            apiRequestNew<T_CertificatesListRes>('GET', '/era/gift/certificate?offset=0&limit=9999'),
            apiRequestNew<T_CertificatesListRes>(
              'GET',
              '/era/gift/certificate?archive=y&offset=0&limit=9999'
            ),
          ])

          const pendingIds = new Set(pendingFiltered.map((p) => p.cert.id))
          const pendingRestoreIds = new Set(
            readPendingRestoreQueue(userId)
              .filter((p) => !pendingIds.has(p.cert.id))
              .map((p) => p.cert.id)
          )
          const serverArchivedIds = new Set((serverArchived || []).map((c) => c.id))
          const serverActiveFiltered = (serverActive || []).filter((c) => (
            !pendingIds.has(c.id)
            && (!serverArchivedIds.has(c.id) || pendingRestoreIds.has(c.id))
          ))
          const serverActiveIds = new Set(serverActiveFiltered.map((c) => c.id))
          const archivedMerged = [
            ...pendingFiltered.map((p) => p.cert),
            ...(serverArchived || []),
          ]
            .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)

          queryClient.setQueryData<T_CertificatesListRes>(
            [ rqKeys.certificatesList ],
            (old) => {
              const fallbackRecentReal = (old || []).filter((c) => {
                if (!(Number(c.id) > 0)) return false
                if (pendingIds.has(c.id)) return false
                if (serverActiveIds.has(c.id)) return false
                if (serverArchivedIds.has(c.id)) return false
                if (typeof c.code !== 'string' || c.code.trim() === '') return false
                const t = normalizeTimeMs(c.time)
                if (!t) return false
                return (now - t) <= recentRealGraceMs
              })
              return sortCertificatesForUi([ ...serverActiveFiltered, ...fallbackRecentReal ])
                .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
            }
          )
          queryClient.setQueryData<T_CertificatesListRes>(
            [ rqKeys.certificatesList, 'archived' ],
            sortCertificatesForUi(archivedMerged)
          )
          // Re-apply optimistic purchase queue after hard merge from server.
          applyPendingPurchaseQueueToCache(userId)
          // Re-apply optimistic restore queue after hard merge from server.
          applyPendingRestoreQueueToCache(userId)

          const activeIds = new Set((serverActive || []).map((c) => c.id))
          const archivedIds = new Set((serverArchived || []).map((c) => c.id))
          const nextConfirmHits = { ...pendingArchiveConfirmHitsRef.current }
          const remainingPending = pendingFiltered.filter((item) => {
            const isStillActive = activeIds.has(item.cert.id)
            const isInArchivedSlice = archivedIds.has(item.cert.id)
            // Confirm archive strictly by server archived slice presence.
            // Time-based fallback caused false confirmations on temporary backend lag.
            const isConfirmedNow = !isStillActive && isInArchivedSlice
            const certId = Number(item.cert.id)
            nextConfirmHits[certId] = isConfirmedNow ? ((nextConfirmHits[certId] || 0) + 1) : 0
            const isConfirmed = nextConfirmHits[certId] >= 3
            return !isConfirmed
          })
          const pendingIdsForHits = new Set(pendingFiltered.map((p) => Number(p.cert.id)))
          for (const certIdStr of Object.keys(nextConfirmHits)) {
            if (!pendingIdsForHits.has(Number(certIdStr))) {
              delete nextConfirmHits[Number(certIdStr)]
            }
          }
          pendingArchiveConfirmHitsRef.current = nextConfirmHits

          if (remainingPending.length !== pendingFiltered.length) {
            const pendingFilteredIds = new Set(pendingFiltered.map((p) => p.cert.id))
            const untouched = pending.filter((item) => !pendingFilteredIds.has(item.cert.id))
            writePendingArchiveQueue(userId, [ ...remainingPending, ...untouched ])
          }
          logHomeCertFlow('schedulePendingArchiveConfirmation:done', {
            delay,
            remainingPendingIds: remainingPending.map((p) => p.cert.id),
          })
        } catch {
          logHomeCertFlow('schedulePendingArchiveConfirmation:error', { delay })
          // ignore - next tick will retry
        } finally {
          isPendingArchiveSyncInFlightRef.current = false
        }
      }, delay)

      pendingArchiveSyncTimersRef.current.push(tid)
    }
  }

  const schedulePendingRestoreConfirmation = (userId: number | null | undefined) => {
    clearPendingRestoreSyncTimers()

    const delays = [
      0,
      getMs('1500ms'),
      getMs('4s'),
      getMs('8s'),
      getMs('16s'),
      getMs('30s'),
      getMs('2m'),
      getMs('5m'),
    ]

    for (const delay of delays) {
      const tid = window.setTimeout(async () => {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return
        const pendingArchiveIds = new Set(
          readPendingArchiveQueue(userId).map((p) => p.cert.id)
        )
        const pending = readPendingRestoreQueue(userId)
          .filter((p) => !pendingArchiveIds.has(p.cert.id))
          .sort((a, b) => b.movedAt - a.movedAt)
        if (!pending.length) return
        logHomeCertFlow('schedulePendingRestoreConfirmation:tick', {
          delay,
          pendingIds: pending.map((p) => p.cert.id),
        })

        try {
          await flushPendingRestoreQueueToServer(userId)
          const [ serverActive, serverArchived ] = await Promise.all([
            apiRequestNew<T_CertificatesListRes>('GET', '/era/gift/certificate?offset=0&limit=9999'),
            apiRequestNew<T_CertificatesListRes>('GET', '/era/gift/certificate?archive=y&offset=0&limit=9999'),
          ])

          const pendingIds = new Set(pending.map((p) => p.cert.id))
          const activeMerged = [
            ...pending.map((p) => p.cert),
            ...(serverActive || []),
          ]
            .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
          const archivedMerged = (serverArchived || [])
            .filter((c) => !pendingIds.has(c.id))

          queryClient.setQueryData<T_CertificatesListRes>(
            [ rqKeys.certificatesList ],
            sortCertificatesForUi(activeMerged)
          )
          queryClient.setQueryData<T_CertificatesListRes>(
            [ rqKeys.certificatesList, 'archived' ],
            sortCertificatesForUi(archivedMerged)
          )
          applyPendingPurchaseQueueToCache(userId)
          applyPendingArchiveQueueToCache(userId)
          applyPendingRestoreQueueToCache(userId)

          const activeIds = new Set((serverActive || []).map((c) => c.id))
          const archivedIds = new Set((serverArchived || []).map((c) => c.id))
          const remainingPending = pending.filter((item) => {
            const isInActiveSlice = activeIds.has(item.cert.id)
            const isStillArchived = archivedIds.has(item.cert.id)
            // Confirm restore strictly by server active slice presence.
            // Time-based fallback caused false confirmations under eventual consistency.
            const isConfirmed = isInActiveSlice && !isStillArchived
            return !isConfirmed
          })

          if (remainingPending.length !== pending.length) {
            const remainingIds = new Set(remainingPending.map((p) => p.cert.id))
            const confirmedIds = pending
              .map((p) => p.cert.id)
              .filter((id) => !remainingIds.has(id))

            // Defensive conflict cleanup:
            // if restore is confirmed, make sure stale archive queue can't pull
            // certificate back out of active list on subsequent sync ticks.
            for (const certId of confirmedIds) {
              removePendingArchiveQueueItem(userId, Number(certId))
            }
          }

          if (remainingPending.length !== pending.length) {
            writePendingRestoreQueue(userId, remainingPending)
          }
          logHomeCertFlow('schedulePendingRestoreConfirmation:done', {
            delay,
            remainingPendingIds: remainingPending.map((p) => p.cert.id),
          })
        } catch {
          logHomeCertFlow('schedulePendingRestoreConfirmation:error', { delay })
          // ignore - next tick will retry
        }
      }, delay)

      pendingRestoreSyncTimersRef.current.push(tid)
    }
  }

  // Apply pending queues before paint to avoid flash on page reload.
  useLayoutEffect(() => {
    if (!userData.id) return
    applyPendingArchiveQueueToCache(userData.id)
    applyPendingRestoreQueueToCache(userData.id)
    applyPendingPurchaseQueueToCache(userData.id)
  }, [ userData.id, applyPendingPurchaseQueueToCache ])

  // Flush offline archived-certificate queue globally (works even when drawer is closed).
  useEffect(() => {
    if (!userData.id) return

    let inFlight = false
    let needsRerun = false
    let onlineQueueEventTimer: number | null = null
    let watchdogFlushTimer: number | null = null
    const scheduleWatchdogFlush = () => {
      if (watchdogFlushTimer !== null) {
        window.clearTimeout(watchdogFlushTimer)
      }
      watchdogFlushTimer = window.setTimeout(() => {
        watchdogFlushTimer = null
        const pendingArchiveNow = readPendingArchiveQueue(userData.id)
        const pendingRestoreNow = readPendingRestoreQueue(userData.id)
        if (!pendingArchiveNow.length && !pendingRestoreNow.length) return
        void flushPendingArchiveQueue()
      }, getMs('5m'))
    }
    const clearWatchdogFlush = () => {
      if (watchdogFlushTimer !== null) {
        window.clearTimeout(watchdogFlushTimer)
        watchdogFlushTimer = null
      }
    }
    const flushPendingArchiveQueue = async (event?: Event) => {
      if (inFlight) {
        needsRerun = true
        return
      }
      const isQueueEvent = (
        event?.type === PENDING_ARCHIVE_UPDATED_EVENT
        || event?.type === PENDING_RESTORE_UPDATED_EVENT
      )
      const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false
      // For online actions, UI mutation already updates cache and sends request.
      // Skip immediate queue-triggered flush to avoid extra jitter/refetch loops.
      // But if queue item gets "stuck" (e.g. transient network error), run a delayed retry.
      if (isQueueEvent && isOnline) {
        const pendingArchiveNow = readPendingArchiveQueue(userData.id)
        const pendingRestoreNow = readPendingRestoreQueue(userData.id)
        if (!pendingArchiveNow.length && !pendingRestoreNow.length) {
          return
        }
        // Apply optimistic queue state immediately to avoid brief reappearance
        // when user quickly navigates to home after going online.
        applyPendingArchiveQueueToCache(userData.id)
        applyPendingRestoreQueueToCache(userData.id)
        applyPendingPurchaseQueueToCache(userData.id)

        if (onlineQueueEventTimer !== null) {
          window.clearTimeout(onlineQueueEventTimer)
        }
        onlineQueueEventTimer = window.setTimeout(() => {
          onlineQueueEventTimer = null
          const pendingArchiveLater = readPendingArchiveQueue(userData.id)
          const pendingRestoreLater = readPendingRestoreQueue(userData.id)
          if (!pendingArchiveLater.length && !pendingRestoreLater.length) return
          void flushPendingArchiveQueue()
        }, getMs('3s'))
        logHomeCertFlow('flushPendingArchiveQueue:online-queue-delayed', {
          eventType: event?.type,
          pendingArchiveNow: pendingArchiveNow.length,
          pendingRestoreNow: pendingRestoreNow.length,
        })
        return
      }
      applyPendingArchiveQueueToCache(userData.id)
      applyPendingRestoreQueueToCache(userData.id)
      applyPendingPurchaseQueueToCache(userData.id)
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      inFlight = true
      try {
        logHomeCertFlow('flushPendingArchiveQueue:start', {
          eventType: event?.type,
          pendingArchive: readPendingArchiveQueue(userData.id).length,
          pendingRestore: readPendingRestoreQueue(userData.id).length,
        })
        await Promise.allSettled([
          flushPendingArchiveQueueToServer(userData.id),
          flushPendingRestoreQueueToServer(userData.id),
        ])
        applyPendingArchiveQueueToCache(userData.id)
        applyPendingRestoreQueueToCache(userData.id)
        applyPendingPurchaseQueueToCache(userData.id)

        const pendingArchiveLeft = readPendingArchiveQueue(userData.id)
        if (pendingArchiveLeft.length) {
          schedulePendingArchiveConfirmation(userData.id)
        } else {
          clearPendingArchiveSyncTimers()
        }
        const pendingRestoreLeft = readPendingRestoreQueue(userData.id)
        if (pendingRestoreLeft.length) {
          schedulePendingRestoreConfirmation(userData.id)
        } else {
          clearPendingRestoreSyncTimers()
        }
        if (pendingArchiveLeft.length || pendingRestoreLeft.length) {
          scheduleWatchdogFlush()
        } else {
          clearWatchdogFlush()
        }
        logHomeCertFlow('flushPendingArchiveQueue:done', {
          pendingArchiveLeft: pendingArchiveLeft.length,
          pendingRestoreLeft: pendingRestoreLeft.length,
        })
      } finally {
        inFlight = false
        if (needsRerun) {
          needsRerun = false
          void flushPendingArchiveQueue()
        }
      }
    }

    void flushPendingArchiveQueue()
    window.addEventListener('online', flushPendingArchiveQueue)
    window.addEventListener(PENDING_ARCHIVE_UPDATED_EVENT, flushPendingArchiveQueue)
    window.addEventListener(PENDING_RESTORE_UPDATED_EVENT, flushPendingArchiveQueue)

    return () => {
      if (onlineQueueEventTimer !== null) {
        window.clearTimeout(onlineQueueEventTimer)
        onlineQueueEventTimer = null
      }
      clearWatchdogFlush()
      clearPendingArchiveSyncTimers()
      clearPendingRestoreSyncTimers()
      window.removeEventListener('online', flushPendingArchiveQueue)
      window.removeEventListener(PENDING_ARCHIVE_UPDATED_EVENT, flushPendingArchiveQueue)
      window.removeEventListener(PENDING_RESTORE_UPDATED_EVENT, flushPendingArchiveQueue)
    }
  }, [ userData.id ])

  useEffect(() => {
    if (!userData.id) return

    let disposed = false
    let inFlight = false
    let queuedRerun = false

    const refreshPendingPurchase = async () => {
      if (disposed) return
      if (inFlight) {
        queuedRerun = true
        return
      }
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      const pending = readPendingPurchaseQueue(userData.id)
      if (!pending.length) return

      inFlight = true
      try {
        await safeRefreshCertificatesListRef.current()
      } catch {
        // Ignore: next tick/online event will retry.
      } finally {
        inFlight = false
        if (!disposed) {
          applyPendingPurchaseQueueToCache(userData.id)
          if (queuedRerun) {
            queuedRerun = false
            void refreshPendingPurchase()
          }
        }
      }
    }

    const reapplyPendingPurchase = () => {
      applyPendingPurchaseQueueToCache(userData.id)
      void refreshPendingPurchase()
    }

    reapplyPendingPurchase()
    const pendingPurchaseIntervalId = window.setInterval(() => {
      void refreshPendingPurchase()
    }, getMs('30s'))
    window.addEventListener('online', refreshPendingPurchase)
    window.addEventListener(PENDING_PURCHASE_UPDATED_EVENT, reapplyPendingPurchase)
    return () => {
      disposed = true
      window.clearInterval(pendingPurchaseIntervalId)
      window.removeEventListener('online', refreshPendingPurchase)
      window.removeEventListener(PENDING_PURCHASE_UPDATED_EVENT, reapplyPendingPurchase)
    }
  }, [ userData.id, applyPendingPurchaseQueueToCache ])

  useEffect(() => {
    const onQueueChanged = () => setQueueVersion((v) => v + 1)
    window.addEventListener(PENDING_ARCHIVE_UPDATED_EVENT, onQueueChanged)
    window.addEventListener(PENDING_RESTORE_UPDATED_EVENT, onQueueChanged)
    return () => {
      window.removeEventListener(PENDING_ARCHIVE_UPDATED_EVENT, onQueueChanged)
      window.removeEventListener(PENDING_RESTORE_UPDATED_EVENT, onQueueChanged)
    }
  }, [])

  const [ isExistCertificatesDrawerVisible, setIsExistCertificatesDrawerVisible ] = useState<boolean>(false)
  const showExistCertificatesDrawer = () => {
    setIsExistCertificatesDrawerVisible(true)

    // Explicit refetch when user opens certificates list.
    // This keeps the cache warm, but avoids constant background requests.
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      void safeRefreshCertificatesList()
      queryClient.refetchQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], exact: true })
        .finally(() => {
          applyPendingArchiveQueueToCache(userData.id)
          applyPendingRestoreQueueToCache(userData.id)
        })
    }
  }
  const hideExistCertificatesDrawer = () => setIsExistCertificatesDrawerVisible(false)


  const [ isCertificateDetailsDrawerVisible, setIsCertificateDetailsDrawerVisible ] = useState<boolean>(false)
  const showCertificateDetailsDrawer = () => setIsCertificateDetailsDrawerVisible(true)
  const hideCertificateDetailsDrawer = () => setIsCertificateDetailsDrawerVisible(false)

  const [ selectedCertificateId, setSelectedCertificateId ] = useState<number>()
  const [ selectedCertificateType, setSelectedCertificateType ] = useState<E_CertificateTypes | null>(null)

  const openCertificateDetailsDrawer = (certificateId: number, goodsId: number) => {
    setSelectedCertificateId(certificateId)
    setSelectedCertificateType(getCertificateType(goodsId) as E_CertificateTypes | null)
    showCertificateDetailsDrawer()
  }

  const [ isBuyCertificateDrawerVisible, setIsBuyCertificateDrawerVisible ] = useState<boolean>(false)
  const showBuyCertificateDrawer = () => setIsBuyCertificateDrawerVisible(true)
  const hideBuyCertificateDrawer = () => setIsBuyCertificateDrawerVisible(false)


  // Запросы на мгновенное пополнение
  const [ isChecksHistoryDrawerVisible, setIsChecksHistoryDrawerVisible ] = useState<boolean>(false)
  const showChecksHistoryDrawer = () => setIsChecksHistoryDrawerVisible(true)
  const hideChecksHistoryDrawer = () => setIsChecksHistoryDrawerVisible(false)


  // Транзакции

  const [ isTransactionsDrawerVisible, setIsTransactionsDrawerVisible ] = useState<boolean>(false)
  const showTransactionsDrawer = () => setIsTransactionsDrawerVisible(true)
  const hideTransactionsDrawer = () => setIsTransactionsDrawerVisible(false)


  const [ isTopUpBalanceDrawerVisible, setIsTopUpBalanceDrawerVisible ] = useState<boolean>(false)
  const showTopUpBalanceDrawer = () => setIsTopUpBalanceDrawerVisible(true)
  const hideTopUpBalanceDrawer = () => setIsTopUpBalanceDrawerVisible(false)
  const mainAccountLabel = 'Основной счёт'
  const cardBalanceLabel = 'Карта'


  return <>
    <SC_PageHomeWrapper>
      {/* Раздел пайщика и счёт */}
      <SC_BalanceCardsWrapper>
        <DashboardMessages />

        {/* Основной счёт */}
        <SC_BalanceCardTop>
          <SC_BalanceAndTransactionsWrapper>
            <Flex vertical align='center' gap={5}>
              <SC_LabelCommonBalance>
                {mainAccountLabel}
              </SC_LabelCommonBalance>

              <SC_BalanceAmount id='tutorial-balance'>
                <span>
                  {isNumber(userData?.mainAccount)
                    ? formatMoney(userData?.mainAccount || 0)
                    : <Skeleton count={1} />
                  }
                </span>

                {!(userData?.sourceCompanyId !== 0 && profile.functionalAll) && (
                  <div onClick={guardOnline(showTopUpBalanceDrawer)} style={{ cursor: 'pointer' }}>
                    <img src={iconPlusSvg} />
                  </div>
                )}
              </SC_BalanceAmount>
            </Flex>
          </SC_BalanceAndTransactionsWrapper>
        </SC_BalanceCardTop>

        {/* Баланс карты */}
        {shouldShowCardBalance && userCards.map((card, i) => {
          const cardBalanceQuery = userCardBalanceQueries[i]
          const cardBalance = Number(cardBalanceQuery?.data?.balance)
          const cardBalanceValue = Number.isFinite(cardBalance) ? cardBalance : 0
          const isCardBalanceError = !!cardBalanceQuery?.isError
          const cardBalanceWarningText = (() => {
            if (cardBalanceQuery?.isPending || isCardBalanceError) return ''
            if (cardBalanceQuery?.data?.is_actual === false) {
              return 'Ошибка при получении баланса из банка'
            }
            return ''
          })()

          return (
            <SC_BalanceCardBottom key={card?.id || `card-balance-${i}`}>
              <SC_BalanceAndTransactionsWrapper>
                <Flex vertical align='center' gap={5} style={{ position: 'relative', width: '100%' }}>
                  <SC_LabelCommonBalance>
                    {`${cardBalanceLabel} ${card?.title?.ru || ''}`}
                  </SC_LabelCommonBalance>

                  <SC_BalanceAmount style={{ position: 'relative' }}>
                    <span>{
                      cardBalanceQuery?.isPending
                        ? <Skeleton count={1} />
                        : isCardBalanceError
                          ? (
                            <span
                              style={{
                                fontSize: 18,
                                fontWeight: 500,
                                lineHeight: '22px',
                                display: 'inline-block',
                                transform: 'translateY(-4px)',
                              }}
                            >
                              Ошибка получения баланса
                            </span>
                          )
                          : formatMoney(cardBalanceValue, false, false, true)
                    }</span>

                    {!(userData?.sourceCompanyId !== 0 && profile.functionalAll) && (
                      <SC_BalancePlusDisabled>
                        <img src={iconPlusSvg} />
                      </SC_BalancePlusDisabled>
                    )}

                  </SC_BalanceAmount>

                  {!!cardBalanceWarningText && (
                    <span
                      title={cardBalanceWarningText}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '63%',
                        transform: 'translateY(-50%)',
                        cursor: 'help',
                      }}
                    >
                      <img
                        src={iconBalanceWarningSvg}
                        alt='warning'
                        style={{
                          width: 28,
                          height: 26,
                        }}
                      />
                    </span>
                  )}
                </Flex>
              </SC_BalanceAndTransactionsWrapper>
            </SC_BalanceCardBottom>
          )
        })}
        {!!waitingChecks?.length && accountsForTesting.includes(userData.id!) && (
          <div
            onClick={showChecksHistoryDrawer}
            style={{
              marginTop: 12,
              textAlign: 'center',
              color: '#1890ff',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            Статус мгновенных пополнений
          </div>
        )}

        <ShareholderArea
          hideBuyCertificateDrawer={hideBuyCertificateDrawer}
        />
      </SC_BalanceCardsWrapper>

      {/* Продуктовые сертификаты */}
      <SC_RoundedCard>
        <SC_GoToAllCertificates
          id='tutorial-go-to-certificates'
          onClick={showExistCertificatesDrawer}
        >
          <span>Продуктовые сертификаты</span>
          <img src={iconArrowRightSvg} />
        </SC_GoToAllCertificates>

        <SC_CreatedCertificatesList>
          {isCertificatesListPending ? (
            <SC_SkeletonWrapper>
              <Skeleton count={1} />
            </SC_SkeletonWrapper>
          ) : <>
            {/* Кнопка добавления сертификата */}
            <SC_AddCertificate
              id='tutorial-add-certificate'
              style={activeCertificatesForUi.length ? { padding: '50px 0' } : {}}
              onClick={guardShareholder(showOrganizationsDrawer)}
            >
              <Button
                size='small'
                ghost
                icon={<img src={iconPlusSvg} />}
                data-add
              >
                {!activeCertificatesForUi.length ? 'Приобрести' : ''}
              </Button>
            </SC_AddCertificate>

            {/* Короткий список сертификатов */}
            {activeCertificatesForUi.slice(0, 5).map((c) => (
              <SC_CertificateCard
                key={c.id}
                onClick={c.id > 0 ? () => openCertificateDetailsDrawer(c.id, c.goods) : undefined}
              >
                <OrganizationBanner
                  isShowId
                  certificateId={c.id}
                  certificateType={getCertificateType(c.goods)}
                />

                <SC_DenominationAndQR>
                  {(() => {
                    const key = `${c.id}-${c.goods}`
                    const restEntry = restMap[key]
                    const rest = restEntry?.value
                    const ts = restEntry?.ts
                    const nominal = getCertificateDenomination(c.goods) || 0

                    return (
                      <SC_Denomination>
                        {rest !== undefined ? (
                          <span>{`${
                            formatMoney(Math.round(rest), false, true)
                          } из ${
                            formatMoney(Math.round(nominal / 100))
                          }`}</span>
                        ) : (
                          <span>{formatMoney(nominal, true)}</span>
                        )}

                        <span style={{ whiteSpace: 'nowrap' }}>{
                          !rest
                            ? 'Номинал'
                            : `Доступно ${ts ? ` на ${formatTs(ts)}` : ''}`}
                        </span>
                      </SC_Denomination>
                    )
                  })()}

                  <SC_QR src={fakeQRPng} />
                </SC_DenominationAndQR>

                <SC_CertId>
                  {c.id <= 0 ? 'Сертификат оформляется' : `#${c.id}`}
                </SC_CertId>
              </SC_CertificateCard>
            ))}
          </>}
        </SC_CreatedCertificatesList>
      </SC_RoundedCard>

      {isExistCertificatesDrawerVisible && (
        <ExistCertificatesDrawer
          certificatesList={activeCertificatesForUi}
          archivedCertificatesList={archivedCertificatesList || []}
          setSelectedCertificateId={setSelectedCertificateId}
          setSelectedCertificateType={setSelectedCertificateType}
          hideExistCertificatesDrawer={hideExistCertificatesDrawer}
          showCertificateDetailsDrawer={showCertificateDetailsDrawer}
          isArchivedCertificatesMode={isArchivedCertificatesMode}
          setCertificatesListToArchivedMode={setCertificatesListToArchivedMode}
          setCertificatesListToRegularMode={setCertificatesListToRegularMode}
        />
      )}

      {isCertificateDetailsDrawerVisible && (
        <CertificateDetailsDrawer
          certificatesList={
            isArchivedCertificatesMode
              ? (archivedCertificatesList || [])
              : activeCertificatesForUi
          }
          isArchivedMode={isArchivedCertificatesMode}
          certificateType={selectedCertificateType ?? undefined}
          selectedCertificateId={selectedCertificateId}
          hideCertificateDetailsDrawer={hideCertificateDetailsDrawer}
        />
      )}

      {isOrganizationsDrawerVisible && (
        <OrganizationsDrawer
          hideOrganizationsDrawer={hideOrganizationsDrawer}
          showBuyCertificateDrawer={showBuyCertificateDrawer}
        />
      )}

      {isBuyCertificateDrawerVisible && (
        <BuyCertificateDrawer
          hideBuyCertificateDrawer={hideBuyCertificateDrawer}
          showOrganizationsDrawer={showOrganizationsDrawer}
        />
      )}

      {isTopUpBalanceDrawerVisible && (
        <TopUpBalanceDrawer
          onClose={hideTopUpBalanceDrawer}
          skipMethodSelection
        />
      )}

      {isChecksHistoryDrawerVisible && (
        <ChecksHistoryDrawer
          onClose={hideChecksHistoryDrawer}
        />
      )}

      {/* Транзакции */}
      <SC_RoundedCard>
        <SC_GoToAllTransactions id='tutorial-transactions' onClick={showTransactionsDrawer}>
          <span>Транзакции</span>
          <img src={iconArrowRightSvg} />
        </SC_GoToAllTransactions>

        <TransactionsList
          transactionsList={transactionsList?.data?.list.slice(0,2) ?? []}
          isPending={isTransactionsListPending}
        />

        {isTransactionsDrawerVisible && (
          <TransactionsDrawer
            hideTransactionsDrawer={hideTransactionsDrawer}
          />
        )}
      </SC_RoundedCard>

      {/* Новости */}
      {profile.isShowNews && <NewsWidget />}
    </SC_PageHomeWrapper>

    <ModalBecomeShareholderForFullAccess
      open={isModalBecomeShareholderOpen}
      onOk={handleOk}
    />
  </>
}
